import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useClipboard from "react-use-clipboard";

import * as Fonts from "../../../../../../components/Fonts";
import LaunchIcon from "../../../../../../components/Launch";
import { PlusTag } from "../../../../../../components/PlusTag";
import { SubHeader } from "../../../../../../components/SubHeader";
import constants from "../../../../../../constants";
import { useAppState } from "../../../../../../contexts/App";
import { useSite } from "../../../../../../contexts/Site";
import CheckIcon from "../../../../../../icons/Check";
import ChevronRightIcon from "../../../../../../icons/ChevronRight";
import { hasActiveSubscription } from "../../../../../../services/api";
import { customDomainService } from "../../../../../../services/customDomain";
import { classNames } from "../../../../../../utils/styles";
import { extractUrlParam } from "../../../../../../utils/url";
import { Container, LinkCard, List, Section, SuccessCard } from "./styles";

const CopyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

/** One address of the Keepo with its own copy/open actions. */
const LinkRow = ({ url, tag }: { url: string; tag?: React.ReactNode }) => {
  const { t } = useTranslation();
  const [isCopied, setCopied] = useClipboard(url, { successDuration: 3000 });
  const prefix = "pages.editor.successPublish.actions";
  const copyLabel = isCopied ? t(`${prefix}.copiedLink`) : t(`${prefix}.copyLink`);

  return (
    <div className="row">
      <div className="address">
        <span className="url" title={url}>
          {url}
        </span>
        {tag}
      </div>
      <button
        type="button"
        className={classNames("action", { copied: isCopied })}
        aria-label={copyLabel}
        title={copyLabel}
        onClick={setCopied}>
        {isCopied ? <CheckIcon width={14} height={14} color="currentColor" /> : <CopyIcon />}
        <span className="label">{copyLabel}</span>
      </button>
      <button
        type="button"
        className="action"
        aria-label={t(`${prefix}.openSite`)}
        title={t(`${prefix}.openSite`)}
        onClick={() => window.open(`//${url}`)}>
        <LaunchIcon color="currentColor" />
        <span className="label">{t(`${prefix}.openSite`)}</span>
      </button>
    </div>
  );
};

const PublishSuccessPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { site } = useSite();
  const { appState } = useAppState();

  const isFirstPublish = extractUrlParam("first") === "true";
  const isPlus = hasActiveSubscription(appState.editingSiteSubscription);
  const keepoUrl = `${site?.url}.${constants.nameSite}`;

  // A connected custom domain gets its own row, above the Keepo address
  // Until the domain is known the card shows a placeholder, not the "no domain" version that could
  // flash and then change. FREE Keepos can't have one, so they don't wait.
  const [customDomain, setCustomDomain] = useState<string>();
  const [isDomainLoaded, setDomainLoaded] = useState(!isPlus);
  useEffect(() => {
    if (!isPlus || !appState.editingSiteId) {
      setDomainLoaded(true);
      return;
    }
    customDomainService
      .get(appState.editingSiteId)
      .then(domain => setCustomDomain(domain?.status === "connected" ? domain.domain : undefined))
      .finally(() => setDomainLoaded(true));
  }, [appState.editingSiteId, isPlus]);

  const prefix = "pages.editor.successPublish";
  const variant = isFirstPublish ? "first" : "republish";

  const nextSteps = [
    { key: "analytics", to: "/analytics" },
    { key: "edit", to: "/editor/pages" },
  ];

  return (
    <Container>
      {/* The screen before this is the publish flow, so the crumb always goes to the dashboard */}
      <SubHeader title={t(`${prefix}.${variant}.header`)} crumbs={[]} noHistoryBack />

      <Section>
        <SuccessCard>
          <span className="check" aria-hidden="true">
            <CheckIcon width={18} height={18} color="currentColor" />
          </span>
          <div className="text">
            <h3>{t(`${prefix}.${variant}.title`)}</h3>
            <p>{t(`${prefix}.${variant}.description`)}</p>
          </div>
        </SuccessCard>
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.link.title`)}</Fonts.Label>
        <LinkCard aria-busy={!isDomainLoaded}>
          {!isDomainLoaded ? (
            <div className="row skeleton" aria-hidden="true">
              <span className="bar" />
              <span className="pill" />
              <span className="pill" />
            </div>
          ) : (
            <>
              {customDomain && <LinkRow url={customDomain} tag={<PlusTag />} />}
              <LinkRow url={keepoUrl} />
              {/* PLUS without a domain: connect one; FREE: the domain is a PLUS feature, so go subscribe */}
              {!customDomain && (
                <button
                  type="button"
                  className="domain-offer"
                  onClick={() =>
                    navigate(
                      isPlus ? "/settings/custom-domain" : `/subscription/features?site=${appState.editingSiteId}`
                    )
                  }>
                  <GlobeIcon />
                  <span className="text">
                    <strong>{t(`${prefix}.link.domain.title`)}</strong>
                    <span>{t(`${prefix}.link.domain.example`)}</span>
                  </span>
                  <span className="side">
                    {!isPlus && <PlusTag />}
                    <span className="cta">
                      {isPlus ? t(`${prefix}.link.domain.connect`) : t(`${prefix}.link.domain.subscribe`)} →
                    </span>
                  </span>
                </button>
              )}
            </>
          )}
        </LinkCard>
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.nextSteps.title`)}</Fonts.Label>
        <List>
          {nextSteps.map(step => (
            <li key={step.key}>
              <button type="button" onClick={() => navigate(step.to)}>
                <span className="text">
                  <span className="title">{t(`${prefix}.nextSteps.${step.key}.title`)}</span>
                  <span className="description">{t(`${prefix}.nextSteps.${step.key}.description`)}</span>
                </span>
                <span className="chevron">
                  <ChevronRightIcon width={12} height={12} color="currentColor" />
                </span>
              </button>
            </li>
          ))}
        </List>
      </Section>
    </Container>
  );
};

export default PublishSuccessPage;
